import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RoomSignalsContext } from "contexts/roomSignals";
import ExpressCheckoutModal from "./ExpressCheckoutModal";
import FeedbackActionModal from "./FeedbackActionModal";
import { getAllRooms, getRoomNumber, updateRoom } from "modules/rooms/store";
import { getSelectedHotel } from "actions/hotel";

const ACTION_FEEDBACK = "FEEDBACK";
const ACTION_EXPRESS = "EXPRESS_CHECKOUT";

const RoomSignalsModals = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const rooms = useSelector(getAllRooms);
    const selectedHotel = useSelector(getSelectedHotel);
    const selectedRoom = useSelector(getRoomNumber);
    const { roomSignals, dataVersion } = useContext(RoomSignalsContext);
    const [stack, setStack] = useState([]); // [{ key, action, type }]
    const seenIds = useRef(new Set());

    const normalizeAction = (value) =>
        typeof value === "string" ? value.replace(/[-_\s]/g, "").toUpperCase() : null;

    const ACTION_MAP = {
        FEEDBACK: ACTION_FEEDBACK,
        EXPRESSCHECKOUT: ACTION_EXPRESS,
    };

    const detectType = (action) => {
        const token = normalizeAction(action?.action);
        return token ? ACTION_MAP[token] ?? null : null;
    };

    const buildKey = (action, index = 0) =>
        action?.id || `${action?.action || "action"}-${action?.projectRef || ""}-${action?.roomNumber || index}`;

    const lastVersionRef = useRef(null);

    const filteredSignals = useMemo(() => {
        const selectedHotelRef = selectedHotel?.ref || selectedHotel;
        if (!roomSignals || !selectedHotelRef || !Number.isFinite(Number(selectedRoom))) return roomSignals;
        return roomSignals.filter(
            (action) => action?.projectRef === selectedHotelRef && Number(action?.roomNumber) === Number(selectedRoom),
        );
    }, [roomSignals, selectedHotel, selectedRoom]);

    useEffect(() => {
        setStack([]);
        seenIds.current.clear();
        lastVersionRef.current = null;
    }, [selectedHotel, selectedRoom]);

    useEffect(() => {
        if (!filteredSignals || stack.length > 0) return;
        if (lastVersionRef.current === dataVersion) return;

        const next = filteredSignals.find((action, index) => {
            const type = detectType(action);
            if (!type) return false;
            const key = buildKey(action, index);
            if (seenIds.current.has(key)) return false;
            return true;
        });

        if (next) {
            const type = detectType(next);
            const key = buildKey(next);
            seenIds.current.add(key);
            setStack([{ key, action: next, type }]);
            lastVersionRef.current = dataVersion;
        }
    }, [filteredSignals, dataVersion, stack.length]);

    const handleClose = (key) => {
        setStack((prev) => {
            const item = prev.find((i) => i.key === key);
            const signalID = Number(item?.action?.id);
            const projectRef = item?.action?.projectRef;
            const signalRoomNumber = Number(item?.action?.roomNumber);

            if (Number.isFinite(signalID) && projectRef && Number.isFinite(signalRoomNumber)) {
                const dismissed = rooms?.[projectRef]?.[signalRoomNumber]?.dismissedSignalIDs;
                const dismissedList = Array.isArray(dismissed)
                    ? dismissed.filter((id) => Number.isFinite(Number(id)))
                    : [];
                const normalizedID = Number(signalID);
                const nextDismissed = dismissedList.includes(normalizedID)
                    ? dismissedList
                    : [...dismissedList, normalizedID].slice(-50);
                const currentLastSignalID = Number(rooms?.[projectRef]?.[signalRoomNumber]?.lastSignalID);
                const nextLastSignalID = Number.isFinite(normalizedID)
                    ? Math.max(
                          normalizedID,
                          Number.isFinite(currentLastSignalID) ? currentLastSignalID : Number.NEGATIVE_INFINITY,
                      )
                    : Number.isFinite(currentLastSignalID)
                      ? currentLastSignalID
                      : null;
                const data = { dismissedSignalIDs: nextDismissed };
                if (Number.isFinite(nextLastSignalID) && nextLastSignalID >= 0) {
                    data.lastSignalID = nextLastSignalID;
                }
                dispatch(
                    updateRoom({
                        projectRef,
                        number: signalRoomNumber,
                        data,
                    }),
                );
            }
            return prev.filter((item) => item.key !== key);
        });
    };

    const handleGoCheckout = (key) => {
        history.push("/checkout");
        handleClose(key);
    };

    if (!stack.length) return null;

    return stack.map((item, index) => {
        const zIndex = 350 + index * 5;
        const backdropOpacity = 0.35;

        if (item.type === ACTION_EXPRESS) {
            return (
                <ExpressCheckoutModal
                    key={item.key}
                    open
                    onClose={() => handleClose(item.key)}
                    onGo={() => handleGoCheckout(item.key)}
                    backdropOpacity={backdropOpacity}
                    style={{ zIndex }}
                />
            );
        }

        return (
            <FeedbackActionModal
                key={item.key}
                open
                onClose={() => handleClose(item.key)}
                backdropOpacity={backdropOpacity}
                style={{ zIndex }}
            />
        );
    });
};

export default RoomSignalsModals;
